import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Home, User, LogOut, ShieldCheck, Tv, Menu, Target, Swords, Crosshair, MessagesSquare, Mail, Gift, Trophy, Wallet, ShoppingBag } from "lucide-react";
import { LogoHorizontal } from "@/components/Logo";
import ChatTicker from "@/components/ChatTicker";
import { Button } from "@/components/ui/button";
import { SURFACE } from "@/lib/surface";

// Build cross-subdomain URLs (admin.* / tv.* / app.*)
const buildSurfaceUrl = (sub, path = "/") => {
  const host = window.location.hostname;
  // strip any leading admin./tv./app. prefix
  const apex = host.replace(/^(admin|tv|app)\./, "");
  const proto = window.location.protocol;
  return `${proto}//${sub ? sub + "." : ""}${apex}${path}`;
};

export const TopNav = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);
  const isAdminSurface = SURFACE === "admin";

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  const links = [
    { to: "/", label: "Trang chủ", icon: Home, testid: "nav-home" },
    { to: "/arena", label: "Đấu trường", icon: Swords, testid: "nav-arena" },
    { to: "/profile", label: "Hồ sơ", icon: User, testid: "nav-profile" },
  ];

  // Mobile menu shows full list since home grid isn't visible
  const mobileLinks = [
    { to: "/", label: "Trang chủ", icon: Home, testid: "mnav-home" },
    { to: "/arena", label: "Đấu trường", icon: Swords, testid: "mnav-arena" },
    { to: "/point-counter", label: "Bắn đền", icon: Crosshair, testid: "mnav-point" },
    { to: "/community", label: "Chat tổng", icon: MessagesSquare, testid: "mnav-chat" },
    { to: "/chat", label: "Tin nhắn", icon: Mail, testid: "mnav-dm" },
    { to: "/leaderboard", label: "Xếp hạng", icon: Trophy, testid: "mnav-lb" },
    { to: "/wallet", label: "Ví tiền", icon: Wallet, testid: "mnav-wallet" },
    { to: "/shop", label: "Cửa hàng", icon: ShoppingBag, testid: "mnav-shop" },
    { to: "/verify", label: "Xác thực", icon: ShieldCheck, testid: "mnav-verify" },
    { to: "/rewards", label: "Đổi quà", icon: Gift, testid: "mnav-rewards" },
    { to: "/profile", label: "Hồ sơ", icon: User, testid: "mnav-profile" },
  ];
  if (user?.role === "admin" || user?.role === "staff") {
    // Admin button points to admin subdomain (if we're on player surface).
    const adminHref = buildSurfaceUrl("admin", "/admin");
    links.push({ to: adminHref, external: true, label: "Quản trị", icon: ShieldCheck, testid: "nav-admin" });
  }

  return (
    <header className="sticky top-0 z-40 border-b border-white/5 bg-[#05050a]/85 backdrop-blur-xl">
      <ChatTicker />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
          <LogoHorizontal size={36} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1 overflow-x-auto">
          {links.map((l) => (
            l.external ? (
              <a
                key={l.to}
                href={l.to}
                data-testid={l.testid}
                className="px-2.5 py-2 rounded-md text-xs whitespace-nowrap flex items-center gap-1.5 transition border border-amber-400/30 text-amber-300 hover:bg-amber-400/10"
              >
                <l.icon size={13} /> {l.label}
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `px-2.5 py-2 rounded-md text-xs whitespace-nowrap flex items-center gap-1.5 transition ${isActive ? "bg-white/8 text-cyan-300" : "text-white/70 hover:text-white hover:bg-white/5"}`
                }
              >
                <l.icon size={13} /> {l.label}
              </NavLink>
            )
          ))}
          <a
            href={buildSurfaceUrl("tv", "/tv")}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-tv"
            className="ml-1 px-2.5 py-2 rounded-md text-xs whitespace-nowrap flex items-center gap-1.5 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10"
          >
            <Tv size={13} /> TV
          </a>
        </nav>

        <div className="flex items-center gap-2">
          {user && (
            <>
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 border border-white/10">
                <img src={user.avatar_url} alt="" className="w-6 h-6 rounded-full" />
                <span className="text-xs text-white/80" data-testid="user-nickname">{user.nickname}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-btn">
                <LogOut size={14} />
              </Button>
            </>
          )}
          <button onClick={() => setOpen(!open)} className="lg:hidden p-2 rounded-md hover:bg-white/5" data-testid="mobile-menu-btn">
            <Menu size={18} />
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/5 px-4 py-2 grid grid-cols-2 gap-1">
          {mobileLinks.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} onClick={() => setOpen(false)} data-testid={l.testid}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm flex items-center gap-2 ${isActive ? "bg-white/10 text-cyan-300" : "text-white/70"}`}
            >
              <l.icon size={15} /> {l.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
};

export default TopNav;
